import type { ReactNode } from "react";
import { Banner, EmptyState, PageHead } from "@ui/react";
import { SettingsIcon } from "@ui/react/icons";
import { useContractor } from "../../lib/auth";
import { parseOperatingMode } from "../../lib/types/enums";
import { routerLink } from "../../lib/routerLink";

/**
 * `OperatingMode` is mutually exclusive — but which screens that actually
 * constrains is narrower than it looks, and getting it wrong creates a
 * catch-22.
 *
 * Verified against the Go: `POST/PATCH/DELETE /profile/services`,
 * `/profile/service-extras` and `PUT /profile/availability` are mounted on the
 * plain `protected` group with NO mode guard. Only `PATCH
 * /profile/direct-booking` answers `409 WRONG_MODE`.
 *
 * That distinction matters because `PATCH /profile/operating-mode` REFUSES to
 * make a contractor bookable until they already have an enabled service and a
 * weekly availability (blockers `noEnabledService` and `noWeeklyAvailability`).
 * If the screens that create those were themselves gated on already being
 * bookable, nobody could ever switch. So the catalogue and availability screens
 * stay open in either mode and merely explain the sequencing; only the booking
 * *rules* screen, whose endpoint genuinely rejects the call, is blocked.
 */
export function useBookableMode(): boolean {
  const contractor = useContractor();
  return parseOperatingMode(contractor.operatingMode) === "bookable";
}

/**
 * Non-blocking. Shown above a setup screen a marketplace contractor is allowed
 * to use, so they understand why they are filling it in.
 */
export function SetupBeforeSwitchBanner() {
  return (
    <Banner tone="info" title="You're set up for marketplace work">
      You can build your service list and hours now — Shoku needs at least one enabled service
      and your weekly availability before it will let you switch to direct bookings. Switch
      from your profile once this is ready.
    </Banner>
  );
}

/**
 * Blocking. Only for the booking-rules screen, whose endpoint genuinely answers
 * 409 WRONG_MODE for a marketplace contractor.
 */
export function MarketplaceModeNotice({ what, body }: { what: string; body?: ReactNode }) {
  const resolvedBody =
    body ??
    `${what} can only be saved once you're taking direct bookings. Add a service and your availability first, then switch your operating mode from your profile.`;

  return (
    <div className="stack stack--loose">
      <PageHead title={what} />
      <EmptyState
        icon={<SettingsIcon />}
        title="You're in marketplace mode"
        action={{ label: "Go to your profile", href: "/profile" }}
        renderLink={routerLink}
      >
        {resolvedBody}
      </EmptyState>
    </div>
  );
}
